import React from "react";
import { CheckCircle, Clock, AlertCircle, XCircle } from "lucide-react";
import { StatusBadge as SharedStatusBadge } from "components/ui/status-badge";
import type { Tone } from "components/ui/tone";

/**
 * Adapter over the shared `StatusBadge`.
 *
 * The original hardcoded `bg-blue-100 text-blue-800` style pairs with no
 * `dark:` variants, so every ticket badge stayed light-on-light in dark mode.
 * The ticket vocabulary, labels, icons, sizes and the descriptive
 * `aria-label` are all preserved; only the colour resolution moved to the
 * shared tone system.
 */

type TicketStatus = "open" | "in_progress" | "resolved" | "closed" | string;

const CONFIG: Record<
    string,
    { label: string; icon: React.ElementType; tone: Tone; description: string }
> = {
    open: {
        label: "Open",
        icon: AlertCircle,
        tone: "info",
        description: "Ticket is open and awaiting response",
    },
    in_progress: {
        label: "In Progress",
        icon: Clock,
        tone: "warning",
        description: "Ticket is being worked on",
    },
    resolved: {
        label: "Resolved",
        icon: CheckCircle,
        tone: "success",
        description: "Ticket has been resolved",
    },
    closed: {
        label: "Closed",
        icon: XCircle,
        tone: "neutral",
        description: "Ticket is closed and completed",
    },
};

const FALLBACK = {
    label: "Unknown",
    icon: AlertCircle,
    tone: "neutral" as Tone,
    description: "Unknown ticket status",
};

const SIZE_MAP = { xs: "xs", sm: "sm", md: "md", lg: "lg" } as const;

interface StatusBadgeProps {
    status: TicketStatus;
    size?: keyof typeof SIZE_MAP;
    ariaLabel?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = "sm", ariaLabel }) => {
    const config = CONFIG[status] ?? FALLBACK;
    const Icon = config.icon;

    return (
        <SharedStatusBadge
            status={status}
            tone={config.tone}
            label={config.label}
            size={SIZE_MAP[size] ?? "sm"}
            icon={<Icon aria-hidden="true" />}
            description={ariaLabel ?? config.description}
        />
    );
};

export default StatusBadge;
